(() => {
  const norm = (value = '') => String(value).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const clean = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();
  const short = (value, limit = 175) => {
    const valueClean = clean(value);
    if (valueClean.length <= limit) return valueClean;
    return `${valueClean.slice(0, limit - 1).replace(/\s+\S*$/, '')}…`;
  };
  const values = (value) => Array.isArray(value) ? value.map(clean).filter(Boolean) : [];
  const unique = (items) => [...new Set(items.map(clean).filter(Boolean))];
  const safeUrl = (value) => {
    try {
      const url = new URL(String(value || ''), location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (_) { return ''; }
  };

  const init = (root) => {
    if (!root || root.dataset.awWhyReady === 'true') return;
    const dataNode = root.querySelector('[data-aw-compatibility-why]');
    const result = root.querySelector('[data-aw-result]');
    const platform = root.querySelector('[data-aw-platform-select]');
    const manufacturer = root.querySelector('[data-aw-manufacturer-select]');
    const replicaType = root.querySelector('[data-aw-replica-type-select]');
    if (!dataNode || !result || !platform || !manufacturer || !replicaType) return;

    let relations;
    try { relations = JSON.parse(dataNode.textContent || '[]'); } catch (_) { return; }
    if (!Array.isArray(relations) || !relations.length) return;

    const box = document.createElement('aside');
    box.className = 'aw-compatibility__why';
    box.hidden = true;
    box.innerHTML = '<div class="aw-compatibility__why-head"><span aria-hidden="true">?</span><strong>Pourquoi compatible ?</strong></div><ul></ul><a hidden target="_blank" rel="noopener noreferrer">Voir la source</a>';
    result.insertAdjacentElement('afterend', box);
    const output = box.querySelector('ul');
    const sourceLink = box.querySelector('a');

    const isEligible = (relation) => {
      const status = norm(relation?.status);
      const score = Number(relation?.score);
      return (status.includes('compatible-verifie') || status.includes('compatible-sous-condition'))
        && Number.isFinite(score) && score >= 90
        && clean(relation?.verificationDate) && clean(relation?.proof);
    };

    const matches = (relation) => {
      if (!platform.value || norm(relation?.platform) !== norm(platform.value)) return false;
      if (relation?.model?.manufacturer) {
        if (!manufacturer.value || norm(relation.model.manufacturer) !== norm(manufacturer.value)) return false;
      }
      if (relation?.model?.replicaType) {
        if (!replicaType.value || norm(relation.model.replicaType) !== norm(replicaType.value)) return false;
      }
      return true;
    };

    const chooseRelation = () => {
      const candidates = relations.filter(isEligible).filter(matches);
      if (!candidates.length) return null;
      const exactModels = unique(candidates.map((relation) => relation?.model?.handle));
      if (exactModels.length > 1) return null;
      candidates.sort((a, b) => Number(Boolean(b?.model?.handle)) - Number(Boolean(a?.model?.handle)) || Number(b.score || 0) - Number(a.score || 0));
      return candidates[0];
    };

    const verifiedFacts = (relation) => (Array.isArray(relation?.facts) ? relation.facts : []).filter((fact) => {
      const status = norm(fact?.status);
      const score = Number(fact?.score);
      return (status.includes('verifie') || status.includes('verified') || status.includes('teste')) && score >= 90 && clean(fact?.value);
    });

    const mechanicalLine = (relation) => {
      const facts = verifiedFacts(relation).slice(0, 2).map((fact) => {
        const factValue = `${clean(fact.value)}${clean(fact.unit) ? ` ${clean(fact.unit)}` : ''}`;
        return clean(fact.category) ? `${clean(fact.category)} : ${factValue}` : factValue;
      });
      if (facts.length) return `Dimensions / standard : ${facts.join(' · ')}`;

      const model = relation?.model || {};
      const standards = [];
      values(model.mountInterfaces).slice(0, 1).forEach((value) => standards.push(value));
      values(model.threads).slice(0, 1).forEach((value) => standards.push(`filetage ${value}`));
      values(model.magazineStandards).slice(0, 1).forEach((value) => standards.push(`chargeur ${value}`));
      if (!standards.length && clean(model.architecture)) standards.push(short(model.architecture, 105));
      return standards.length ? `Standard mécanique : ${unique(standards).join(' · ')}` : '';
    };

    const render = () => {
      const verdictOk = result.classList.contains('aw-compatibility__result--compatible') || result.classList.contains('aw-compatibility__result--conditional');
      const relation = verdictOk ? chooseRelation() : null;
      if (!relation) {
        box.hidden = true;
        output.replaceChildren();
        sourceLink.hidden = true;
        sourceLink.removeAttribute('href');
        return;
      }

      const items = [`Preuve : ${short(relation.proof, 180)}`];
      const model = relation.model || {};
      const reference = unique([model.name, model.reference, relation.generationVersion || model.generationVersion]);
      if (reference.length) items.push(`Référence exacte : ${reference.join(' · ')}`);
      const mechanical = mechanicalLine(relation);
      if (mechanical) items.push(short(mechanical, 180));
      const restrictions = unique([relation.condition, relation.adaptation]);
      if (restrictions.length) items.push(`Restriction : ${short(restrictions.join(' · '), 185)}`);

      output.replaceChildren(...items.slice(0, 4).map((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        return li;
      }));

      const source = safeUrl(relation.sourceUrl) || safeUrl(verifiedFacts(relation).find((fact) => safeUrl(fact.sourceUrl))?.sourceUrl);
      sourceLink.hidden = !source;
      if (source) sourceLink.href = source;
      else sourceLink.removeAttribute('href');
      box.hidden = false;
    };

    const observer = new MutationObserver(render);
    observer.observe(result, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true, characterData: true });
    [platform, manufacturer, replicaType].forEach((control) => control.addEventListener('change', () => queueMicrotask(render)));
    addEventListener('aw:compatibility-changed', () => queueMicrotask(render));
    root.dataset.awWhyReady = 'true';
    render();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-aw-compatibility]').forEach(init);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
